import type { MenuInfo } from '@/common/components/organisms/baseMenu/types';
import { IconType } from '@/types/icon.types';

import {
  ARRAY_FIELDS,
  EXCLUDED_MENU_VALUES,
  OBJECT_FIELDS,
  ROOT_LEAF_MENU_PATH,
} from '@/common/components/organisms/menuSearch/menuSearch.define';

import { isSectionTitle } from './menuSearch.common.utils';

/**
 * 배열 필드를 재귀적으로 필터링
 * @param items - 필터링할 MenuInfo 배열
 * @param keyword - 검색 키워드
 * @param filterFn - 필터링 함수
 * @returns 필터링된 MenuInfo 배열 또는 null
 */
export const filterArrayField = (
  items: MenuInfo[] | undefined,
  keyword: string,
  filterFn: (item: MenuInfo, keyword: string) => MenuInfo | null,
): MenuInfo[] | null => {
  if (!items || items.length === 0) return null;

  const filtered = items
    .map((item) => filterFn(item, keyword))
    .filter((item): item is MenuInfo => item !== null);

  return filtered.length > 0 ? filtered : null;
};

/**
 * 대시보드/분석보드의 subMenuList를 필터링
 * @param dashboardItem - 대시보드 또는 분석보드 MenuInfo
 * @param keyword - 검색 키워드
 * @returns 필터링된 MenuInfo 또는 null
 */
const filterDashboardSubMenuList = (dashboardItem: MenuInfo, keyword: string): MenuInfo | null => {
  if (!dashboardItem.subMenuList || dashboardItem.subMenuList.length === 0) {
    return null;
  }

  const filteredSubMenuList = dashboardItem.subMenuList
    .map((item) => {
      const itemText = item.text?.toLowerCase() || '';
      // 대시보드/분석보드 아이템 자체가 검색어에 매칭되면 포함
      if (itemText.includes(keyword)) {
        return item;
      }
      return null;
    })
    .filter((item): item is MenuInfo => item !== null);

  if (filteredSubMenuList.length === 0) {
    return null;
  }

  return {
    ...dashboardItem,
    subMenuList: filteredSubMenuList,
  };
};

/**
 * subMenuList를 가진 메뉴를 특별한 형태로 변환
 * 검색 키워드와 매칭되는 카테고리의 children을 반환
 * @param menuItem - subMenuList를 가진 메뉴 아이템
 * @param keyword - 검색 키워드 (소문자)
 * @returns 변환된 메뉴 또는 null
 */
const transformSubMenuListMenu = (menuItem: MenuInfo, keyword: string): MenuInfo | null => {
  // subMenuList가 없는 경우 null 반환
  if (!menuItem.subMenuList || menuItem.subMenuList.length === 0) {
    return null;
  }

  // subMenuList에서 검색 키워드와 매칭되는 카테고리 찾기
  // text가 검색 키워드와 정확히 일치하거나 포함하는 경우만 매칭
  const matchedCategory = menuItem.subMenuList.find((item) => {
    // 섹션 타이틀(그룹 헤더)은 자기 자신의 텍스트로 매칭하지 않는다.
    if (isSectionTitle(item)) {
      return false;
    }
    const itemText = item.text?.toLowerCase() || '';
    // 정확히 일치하거나 포함하는 경우만 매칭 (빈 value는 자동 매칭하지 않음)
    if (itemText === keyword || itemText.includes(keyword)) {
      return item.children && item.children.length > 0;
    }
    return false;
  });

  if (!matchedCategory?.children || matchedCategory.children.length === 0) {
    return null;
  }

  // 매칭된 카테고리의 children을 반환
  return {
    text: matchedCategory.text || menuItem.text || '',
    value: matchedCategory.value || '',
    children: matchedCategory.children,
  };
};

/**
 * MenuInfo를 재귀적으로 필터링
 * @param menuItem - 필터링할 MenuInfo
 * @param keyword - 검색 키워드 (소문자)
 * @returns 필터링된 MenuInfo 또는 null
 */
export const filterMenuItemRecursively = (menuItem: MenuInfo, keyword: string): MenuInfo | null => {
  // 제외 대상 메뉴는 필터링하지 않음
  if (EXCLUDED_MENU_VALUES.includes(menuItem.value)) {
    return null;
  }

  const itemText = menuItem.text?.toLowerCase() || '';
  const isTextMatched = itemText === keyword || itemText.includes(keyword);
  // 자식이 모두 leaf 인 섹션 타이틀(그룹 헤더)은 자기 텍스트 매칭을 차단한다.
  // 예: `oracle_Real Time Monitoring`, `logs_logging` 같은 그룹 헤더가 매칭되어
  // children 이 통째로 결과로 노출되는 동작을 차단.
  // path 가 있거나 자식이 또 다른 그룹인 의미 있는 컨테이너(database, oracle, AWS 등)는
  // 자기 텍스트 매칭을 그대로 허용한다.
  const isCurrentMatched = isTextMatched && !isSectionTitle(menuItem);

  // 1-Depth 클릭 루트 메뉴(홈, 대시보드, AIOps)는 매칭될 때만 반환
  // (children이 없어 자식 매칭으로는 살아남을 수 없으므로, path를 채워 동일한 구조로 매핑)
  if (menuItem.value in ROOT_LEAF_MENU_PATH && isTextMatched) {
    return {
      ...menuItem,
      path: menuItem.path || ROOT_LEAF_MENU_PATH[menuItem.value],
    };
  }

  // subMenuList를 가진 메뉴에 대한 특별한 처리
  // kubernetes_network, kubernetes_ConfigStorage, kubernetes_accessControl 등
  if (menuItem.subMenuList && menuItem.subMenuList.length > 0) {
    const transformed = transformSubMenuListMenu(menuItem, keyword);
    if (transformed) {
      return transformed;
    }
  }

  // 쿠버네티스 메뉴 내부에서 subMenuList를 가진 메뉴 찾기
  if (menuItem.value === 'kubernetes' && menuItem.children) {
    // 빈 value를 가진 children 내부에서 subMenuList를 가진 메뉴 찾기
    for (const child of menuItem.children) {
      if (!child.value && child.children) {
        for (const subChild of child.children) {
          if (subChild.subMenuList && subChild.subMenuList.length > 0) {
            const transformed = transformSubMenuListMenu(subChild, keyword);
            if (transformed) {
              return transformed;
            }
          }
        }
      }
    }
  }

  // 빈 value를 가진 아이템의 children에서 subMenuList를 가진 메뉴 찾기
  if (!menuItem.value && menuItem.children) {
    for (const child of menuItem.children) {
      if (child.subMenuList && child.subMenuList.length > 0) {
        const transformed = transformSubMenuListMenu(child, keyword);
        if (transformed) {
          return transformed;
        }
      }
    }
  }

  // 검색 결과에서 제외할 중간 depth 메뉴 (자식은 검색 가능)
  const isExcluded = isSectionTitle(menuItem);

  // 빈 text나 value를 가진 중간 depth 아이템은 검색 대상에서 제외
  // 하지만 자식들은 검색 가능해야 함
  const isEmptyItem = !menuItem.text || !menuItem.value;

  // 검색 결과에서 제외할 메뉴이거나 빈 아이템인 경우 자식만 검색
  if (isExcluded || isEmptyItem) {
    const filteredItem = { ...menuItem };

    // 배열 타입 필드들 필터링 (children, subMenuList)
    // 빈 아이템의 경우 자식들을 재귀적으로 검색해야 함
    const hasMatchedArrayChild = ARRAY_FIELDS.reduce((hasMatched, fieldName) => {
      const fieldValue = menuItem[fieldName];
      if (!fieldValue || fieldValue.length === 0) {
        return hasMatched;
      }

      // 빈 아이템의 자식들을 재귀적으로 필터링
      const filteredArray = fieldValue
        .map((child) => filterMenuItemRecursively(child, keyword))
        .filter((child): child is MenuInfo => child !== null);

      if (filteredArray.length > 0) {
        filteredItem[fieldName] = filteredArray;
        return true;
      }

      delete filteredItem[fieldName];
      return hasMatched;
    }, false);

    // 객체 타입 필드들 필터링 (dashboardList, analysisBoardList)
    const hasMatchedObjectChild = OBJECT_FIELDS.reduce((hasMatched, fieldName) => {
      const dashboardItem = menuItem[fieldName];
      if (!dashboardItem) {
        return hasMatched;
      }

      // 대시보드/분석보드의 subMenuList 필터링
      const filteredDashboard = filterDashboardSubMenuList(dashboardItem, keyword);
      if (filteredDashboard) {
        filteredItem[fieldName] = filteredDashboard;
        return true;
      }

      delete filteredItem[fieldName];
      return hasMatched;
    }, false);

    // 자식 중 하나라도 매칭되면 현재 노드 반환
    return hasMatchedArrayChild || hasMatchedObjectChild ? filteredItem : null;
  }

  // 현재 노드가 매칭되는 경우
  if (isCurrentMatched) {
    const filteredItem = { ...menuItem };

    // 자식들도 재귀적으로 필터링 (하지만 매칭되는 자식이 있으면 포함)
    // 배열 타입 필드들 필터링 (children, subMenuList)
    ARRAY_FIELDS.forEach((fieldName) => {
      const fieldValue = menuItem[fieldName];
      if (!fieldValue || fieldValue.length === 0) {
        return;
      }

      // subMenuList의 경우, 내부 아이템들을 재귀적으로 필터링해야 함
      const filteredArray = fieldValue
        .map((child) => filterMenuItemRecursively(child, keyword))
        .filter((child): child is MenuInfo => child !== null);

      if (filteredArray.length > 0) {
        filteredItem[fieldName] = filteredArray;
      } else {
        // 필터링된 결과가 없어도 원본 유지 (현재 노드가 매칭되었으므로)
        filteredItem[fieldName] = menuItem[fieldName];
      }
    });

    // 객체 타입 필드들 필터링 (dashboardList, analysisBoardList)
    // 검색어에 매칭될 때만 포함
    OBJECT_FIELDS.forEach((fieldName) => {
      const dashboardItem = menuItem[fieldName];
      if (dashboardItem) {
        // 대시보드/분석보드의 subMenuList 필터링
        const filteredDashboard = filterDashboardSubMenuList(dashboardItem, keyword);
        if (filteredDashboard) {
          filteredItem[fieldName] = filteredDashboard;
        } else {
          // 검색어에 매칭되지 않으면 제거
          delete filteredItem[fieldName];
        }
      }
    });

    return filteredItem;
  }

  // 현재 노드가 매칭되지 않으면 자식 노드들을 재귀적으로 검색
  const filteredItem = { ...menuItem };

  // 배열 타입 필드들 필터링 (children, subMenuList)
  const hasMatchedArrayChild = ARRAY_FIELDS.reduce((hasMatched, fieldName) => {
    const filteredArray = filterArrayField(menuItem[fieldName], keyword, filterMenuItemRecursively);

    if (filteredArray) {
      filteredItem[fieldName] = filteredArray;
      return true;
    }

    delete filteredItem[fieldName];
    return hasMatched;
  }, false);

  // 객체 타입 필드들 필터링 (dashboardList, analysisBoardList)
  const hasMatchedObjectChild = OBJECT_FIELDS.reduce((hasMatched, fieldName) => {
    const dashboardItem = menuItem[fieldName];
    if (!dashboardItem) {
      return hasMatched;
    }

    // 대시보드/분석보드의 subMenuList 필터링
    const filteredDashboard = filterDashboardSubMenuList(dashboardItem, keyword);
    if (filteredDashboard) {
      filteredItem[fieldName] = filteredDashboard;
      return true;
    }

    delete filteredItem[fieldName];
    return hasMatched;
  }, false);

  // 자식 중 하나라도 매칭되면 현재 노드 반환
  return hasMatchedArrayChild || hasMatchedObjectChild ? filteredItem : null;
};

/**
 * dashboardMenuInfo와 analysisBoardMenuInfo를 MenuInfo에 매핑
 * @param menuInfo - 원본 MenuInfo 배열
 * @param dashboardMenuInfo - 대시보드 데이터 (key: 메뉴 value, value: 대시보드 배열)
 * @param analysisBoardMenuInfo - 분석보드 데이터 (key: 메뉴 value, value: 분석보드 배열)
 * @returns 대시보드 데이터가 매핑된 MenuInfo 배열
 */
export const mergeDashboardDataToMenuInfo = (
  menuInfo: MenuInfo[],
  dashboardMenuInfo: Record<string, MenuInfo[]>,
  analysisBoardMenuInfo: Record<string, MenuInfo[]>,
): MenuInfo[] => {
  return menuInfo.map((item) => {
    const clonedItem = { ...item };

    // 현재 아이템의 value와 일치하는 대시보드 데이터 찾기
    const dashboards = dashboardMenuInfo[item.value] || [];
    const analysisBoards = analysisBoardMenuInfo[item.value] || [];

    // dashboardList가 존재하고 대시보드 데이터가 있으면 매핑
    if (clonedItem.dashboardList && dashboards.length > 0) {
      clonedItem.dashboardList = {
        ...clonedItem.dashboardList,
        subMenuList: dashboards
          .filter((dashboard) => dashboard.text && dashboard.value)
          .map(
            (dashboard) =>
              ({
                text: dashboard.text!,
                value: dashboard.value!,
                path: dashboard.path,
                favoriteId: dashboard.favoriteId || undefined,
                featuredIcon: (dashboard.featuredIcon as IconType) || undefined,
                children: [],
              }) as MenuInfo,
          ),
      };
    }

    // analysisBoardList가 존재하고 분석보드 데이터가 있으면 매핑
    if (clonedItem.analysisBoardList && analysisBoards.length > 0) {
      clonedItem.analysisBoardList = {
        ...clonedItem.analysisBoardList,
        subMenuList: analysisBoards
          .filter((board) => board.text && board.value)
          .map(
            (board) =>
              ({
                text: board.text!,
                value: board.value!,
                path: board.path,
                favoriteId: board.favoriteId || undefined,
                featuredIcon: (board.featuredIcon as IconType) || undefined,
                children: [],
              }) as MenuInfo,
          ),
      };
    }

    return clonedItem;
  });
};
